import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Plus, Edit2, Trash2, Warehouse, AlertTriangle } from "lucide-react";

const empty = { name: "", location: "", capacity_litres: 20000, current_stock: 0, min_level: 2000 };

export default function Tanks() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["tanks"],
    queryFn: () => api.get("/inventory/tanks").then((r) => r.data),
  });

  const [editing, setEditing] = useState(null);   // full tank doc or null
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || "",
      location: t.location || "",
      capacity_litres: t.capacity_litres || 0,
      current_stock: t.current_stock || 0,
      min_level: t.min_level || 0,
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: (payload) => editing
      ? api.patch(`/inventory/tanks/${editing.id}`, payload)
      : api.post("/inventory/tanks", payload),
    onSuccess: () => {
      toast.success(editing ? "Tank updated" : "Tank created");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["tanks"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/inventory/tanks/${id}`),
    onSuccess: () => { toast.success("Tank deleted"); qc.invalidateQueries({ queryKey: ["tanks"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const rows = data?.items || [];

  return (
    <div className="space-y-4" data-testid="tanks-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Storage Tanks</h1>
          <p className="text-sm text-muted-foreground">
            Manage on-site storage tanks — capacity, current stock, minimum-level alert threshold.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="btn-new-tank"><Plus size={14} className="mr-1" /> New Tank</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? `Edit ${editing.code}` : "New storage tank"}</DialogTitle>
              <DialogDescription>
                {editing
                  ? "Update the tank details below. Codes are immutable."
                  : "Give the tank a friendly name and its total capacity."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Depot Main Tank" data-testid="tank-name" />
              </div>
              <div className="sm:col-span-2">
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Pune Depot · Bay 1" data-testid="tank-location" />
              </div>
              <div>
                <Label>Capacity (L)</Label>
                <Input type="number" value={form.capacity_litres} onChange={(e) => setForm({ ...form, capacity_litres: Number(e.target.value) })} data-testid="tank-capacity" />
              </div>
              <div>
                <Label>Min level alert (L)</Label>
                <Input type="number" value={form.min_level} onChange={(e) => setForm({ ...form, min_level: Number(e.target.value) })} data-testid="tank-min-level" />
              </div>
              {editing && (
                <div className="sm:col-span-2">
                  <Label>Current stock (L)</Label>
                  <Input type="number" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: Number(e.target.value) })} data-testid="tank-current-stock" />
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    Manual edits here are audited but bypass stock-movement entries. Prefer using Procurement → Release or Inventory → Move.
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={() => save.mutate(form)}
                disabled={!form.name || !form.capacity_litres || save.isPending}
                data-testid="btn-save-tank"
              >
                {save.isPending ? "Saving…" : editing ? "Save changes" : "Create tank"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Name / Location</th>
                  <th className="px-4 py-3 text-right">Capacity</th>
                  <th className="px-4 py-3 text-right">Current stock</th>
                  <th className="px-4 py-3">Fill level</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const cap = t.capacity_litres || 0;
                  const stk = t.current_stock || 0;
                  const pct = cap ? Math.round((stk / cap) * 100) : 0;
                  const low = t.min_level && stk < t.min_level;
                  const barColor = low ? "bg-red-500" : pct > 85 ? "bg-amber-500" : "bg-primary";
                  return (
                    <tr key={t.id} className="border-b border-border/40 text-sm" data-testid={`tank-row-${t.code}`}>
                      <td className="px-4 py-3 font-mono font-semibold">{t.code}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.location || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">{cap.toLocaleString("en-IN")} L</td>
                      <td className="px-4 py-3 text-right font-mono font-bold">{Math.round(stk).toLocaleString("en-IN")} L</td>
                      <td className="px-4 py-3">
                        <div className="min-w-[120px]">
                          <div className="mb-1 flex justify-between text-[10px] text-muted-foreground">
                            <span>{pct}%</span>
                            {low && <span className="font-semibold text-red-600"><AlertTriangle size={9} className="mr-0.5 inline" />Low</span>}
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-muted">
                            <div className={`h-1.5 rounded-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`btn-edit-tank-${t.code}`}>
                            <Edit2 size={14} />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              if (window.confirm(`Delete tank ${t.code}? This cannot be undone.`)) del.mutate(t.id);
                            }}
                            data-testid={`btn-delete-tank-${t.code}`}
                          >
                            <Trash2 size={14} className="text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                      <Warehouse size={26} className="mx-auto mb-2 opacity-40" />
                      No tanks yet. Click <b>New Tank</b> to register your first storage tank.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
